import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { fetchAllFilteredRows } from "@/components/shared/data-table/data-table-export";
import { useInlineEditMutation } from "@/components/shared/data-table/use-inline-edit-mutation";
import { invoiceKeys } from "@/features/invoices/hooks/query-keys";
import {
  getInvoice,
  getInvoices,
  getInvoiceTotals,
  updateInvoice,
} from "@/features/invoices/hooks/invoices-client";
import type { Invoice, InvoiceFilters } from "@/features/invoices/types";

export function useInvoice(invoiceId: string) {
  return useQuery({
    enabled: Boolean(invoiceId),
    queryKey: invoiceKeys.detail(invoiceId || "missing"),
    queryFn: () => getInvoice(invoiceId),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePrefetchInvoiceDetail() {
  const queryClient = useQueryClient();
  return useCallback(
    (invoiceId: string) =>
      queryClient.prefetchQuery({
        queryFn: () => getInvoice(invoiceId),
        queryKey: invoiceKeys.detail(invoiceId),
        staleTime: 5 * 60 * 1000,
      }),
    [queryClient],
  );
}

export function useInvoices(filters: InvoiceFilters) {
  return useQuery({
    queryKey: invoiceKeys.list(filters as Record<string, unknown>),
    queryFn: () => getInvoices(filters),
  });
}

/**
 * Totals for the whole filtered set.
 *
 * Keyed on the filters alone — not the page or the sort — so paging through a
 * result does not refetch a figure that cannot have changed, while every
 * change to scope, search, customer or dates does.
 */
export function useInvoiceTotals(filters: InvoiceFilters) {
  const totalsFilters: InvoiceFilters = {
    account_id: filters.account_id,
    date_from: filters.date_from,
    date_to: filters.date_to,
    search: filters.search,
    statuses: filters.statuses,
  };

  return useQuery({
    queryKey: invoiceKeys.totals(totalsFilters as Record<string, unknown>),
    queryFn: () => getInvoiceTotals(totalsFilters),
  });
}

/**
 * The invoice note, edited from the receivables list.
 *
 * Notes are the only invoice field the list writes. Everything else on an
 * invoice is money or the date money is measured from: the balance and the
 * collected figure are derived from payments and credits, and the issue date
 * decides which ageing bucket and which period the invoice reports in. Those
 * move through the invoice's own route, where the consequences are on screen.
 *
 * A note has no such downstream reader, so correcting one while scanning the
 * list costs nothing and is exactly what the list is being read for.
 */
export function useInlineEditInvoiceNotes() {
  return useInlineEditMutation<Invoice, { id: string; notes: string | null }>({
    getRowId: (invoice) => invoice.id,
    // Wider than the list it patches, because the footer is a separate query.
    // A note is part of `search_text`, so editing one under an active search
    // can move the row in or out of the filtered set — and a count that did
    // not follow it would sit under the table contradicting it.
    invalidateKey: invoiceKeys.all,
    listKey: invoiceKeys.lists(),
    mutationFn: ({ id, notes }) => updateInvoice(id, { notes }),
    patch: ({ notes }) => ({ notes }),
  });
}

/**
 * Every invoice the current filters select, for CSV export.
 *
 * Runs the list query without its page window, so what is written to the file
 * is the filtered set rather than the page a user happens to be standing on.
 */
export function fetchInvoicesForExport(filters: InvoiceFilters): Promise<Invoice[]> {
  return fetchAllFilteredRows<Invoice>(({ limit, offset }) =>
    getInvoices({ ...filters, limit, offset }),
  );
}
